import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import type { CheckoutStatus } from "@/lib/types";
import { Alert, Button, Card, PageHeader, Spinner } from "@/components/ui";

/**
 * Where a signed-in customer lands after paying for a booking at the gateway.
 *
 * Coming back here is not proof the money arrived -- the return URL is under
 * the customer's control -- so this polls the status the payment webhook
 * writes once the gateway actually settles. The shipment is booked by that
 * webhook, not by this page, which means closing the tab loses nothing.
 */
export function Checkout() {
  const { paymentId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["checkout", paymentId],
    queryFn: () => api.get<CheckoutStatus>(`/v1/shipments/checkout/${encodeURIComponent(paymentId)}`),
    enabled: paymentId.length > 0,
    refetchInterval: (query) => (query.state.data?.status === "pending" ? 2500 : false),
  });

  // Once it is booked there is nothing left to look at here, so go to the
  // shipment itself -- the same place a wallet booking lands.
  useEffect(() => {
    if (data?.status !== "booked" || !data.shipment_id) return;
    queryClient.invalidateQueries({ queryKey: ["shipments"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    queryClient.invalidateQueries({ queryKey: ["wallet"] });
    navigate(`/app/shipments/${data.shipment_id}?booked=1`, { replace: true });
  }, [data?.status, data?.shipment_id, navigate, queryClient]);

  return (
    <div>
      <PageHeader title="Completing your booking" subtitle="We are confirming the payment with your bank." />

      {(isLoading || data?.status === "pending" || data?.status === "booked") && (
        <Card className="flex flex-col items-center gap-4 py-14 text-center">
          <Spinner className="h-6 w-6" />
          <div>
            <p className="text-lg font-semibold">
              {data?.status === "booked" ? "Paid. Opening your shipment" : "Confirming your payment"}
            </p>
            <p className="mt-1 text-sm text-ink-500">
              This usually takes a few seconds. Do not close this page.
            </p>
            {data?.status === "pending" && data.charged_cents > 0 && (
              <p className="mt-3 text-sm text-ink-500">Paying {money(data.charged_cents)}</p>
            )}
          </div>
        </Card>
      )}

      {data?.status === "failed" && (
        <Card className="py-10 text-center">
          <p className="text-lg font-semibold">We could not complete this booking</p>
          <div className="mx-auto mt-4 max-w-md">
            <Alert tone="warn">
              {data.failure_reason ?? "The payment could not be completed. No shipment was booked."}
            </Alert>
          </div>
          <p className="mt-4 text-sm text-ink-500">
            Nothing was collected and nothing is owed. If your card was charged, it has been refunded.
          </p>
          <Button className="mt-6" onClick={() => navigate("/app/book")}>
            Start the booking again
          </Button>
        </Card>
      )}
    </div>
  );
}
